import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '@/services/api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  UserPlus,
  MoreVertical,
  Mail,
  Eye,
  Trash2,
  RefreshCw,
  Users,
  Building2,
  Phone,
  FileText,
  Loader2,
  Pencil,
  FolderOpen,
  HardDrive,
  FileCheck,
  AlertTriangle,
  Clock,
  CalendarDays,
  Briefcase,
  History,
  CheckCircle2,
  MessageSquare,
  ClipboardList,
  Receipt,
  Activity,
  TrendingUp,
  TrendingDown,
  Link2,
  Unlink,
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PageHeader } from '@/components/shared/PageHeader';
import { clientsService } from '@/services/api/clients';
import { getConnectionStatus, getContacts, connect as connectXero, mapClient as mapXeroClient, remapByAbn, XeroContact } from '@/services/api/xero';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type {
  Client,
  ClientType,
  ClientStatus,
  CreateClientRequest,
  UpdateClientRequest,
  CLIENT_TYPE_LABELS,
  CLIENT_STATUS_LABELS,
  formatFileSize,
  Deadline,
  DeadlineStatus,
  ClientHealthScore,
  HealthScoreLevel,
} from '@/types/client';
import { DEADLINE_STATUS_LABELS } from '@/types/client';

// ClientDeadlines component
function ClientDeadlines({ clientId }: { clientId: string }) {
  const navigate = useNavigate();

  const { data: deadlines, isLoading } = useQuery({
    queryKey: ['client-deadlines', clientId],
    queryFn: () => clientsService.getClientDeadlines(clientId),
  });

  const activeDeadlines = deadlines?.filter(d => d.status !== 'completed') || [];
  const hasDeadlines = activeDeadlines.length > 0;

  const statusColors: Record<DeadlineStatus, string> = {
    overdue: 'bg-red-500/10 text-red-500 border-red-500/20',
    due_soon: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    upcoming: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    completed: 'bg-green-500/10 text-green-500 border-green-500/20',
  };

  const formatDueDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays <= 7) return `in ${diffDays} days`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  if (isLoading) {
    return (
      <>
        <Separator />
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-16 w-full" />
        </div>
      </>
    );
  }

  if (!hasDeadlines) {
    return null;
  }

  return (
    <>
      <Separator />
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Upcoming Deadlines
          </h4>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => navigate('/panel/deadlines')}
          >
            View All
          </Button>
        </div>
        <div className="space-y-2">
          {activeDeadlines.slice(0, 3).map((deadline) => (
            <div
              key={deadline.id}
              className={cn(
                'p-2 rounded-lg border text-xs cursor-pointer transition-colors hover:bg-accent',
                statusColors[deadline.status]
              )}
              onClick={() => navigate('/panel/deadlines')}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{deadline.title}</p>
                  <p className="text-[10px] opacity-70 mt-0.5">
                    {formatDueDate(deadline.dueDate)}
                  </p>
                </div>
                <Badge variant="outline" className={cn('text-[10px] h-5', statusColors[deadline.status])}>
                  {DEADLINE_STATUS_LABELS[deadline.status]}
                </Badge>
              </div>
            </div>
          ))}
          {activeDeadlines.length > 3 && (
            <p className="text-xs text-muted-foreground text-center py-1">
              +{activeDeadlines.length - 3} more deadline{activeDeadlines.length - 3 !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    </>
  );
}

// Xero Organization Connection Section for client detail panel
function XeroMappingSection({ client, onMapped }: { client: Client; onMapped: () => void }) {
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);

  // Fetch available Xero orgs
  const { data: xeroStatus } = useQuery({
    queryKey: ['xero-status'],
    queryFn: getConnectionStatus,
  });

  const availableOrgs = xeroStatus?.organizations || [];

  const handleSelectOrg = async (xeroTenantId: string) => {
    const org = availableOrgs.find((o: any) => o.xero_tenant_id === xeroTenantId);
    if (!org) return;
    try {
      await mapXeroClient(client.id, xeroTenantId, org.xero_tenant_name);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      onMapped();
      toast.success(`Connected to ${org.xero_tenant_name}`);
    } catch {
      toast.error('Failed to connect');
    }
  };

  const handleConnectNewOrg = async () => {
    setIsConnecting(true);
    try {
      const returnUrl = `/panel/clients?selected=${client.id}`;
      const { authUrl } = await connectXero(returnUrl, client.id);
      window.location.href = authUrl;
    } catch {
      toast.error('Failed to connect to Xero');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await mapXeroClient(client.id, null);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      onMapped();
      toast.success('Xero organization disconnected');
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  return (
    <>
      <Separator />
      <div className="space-y-3">
        <h4 className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
          <Link2 className="h-4 w-4" />
          Xero Organization
        </h4>
        {client.xeroOrgName ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-sm truncate">{client.xeroOrgName}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs flex-shrink-0"
              onClick={handleDisconnect}
            >
              <Unlink className="h-3 w-3 mr-1" />
              Disconnect
            </Button>
          </div>
        ) : availableOrgs.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Select the Xero organization for this client:
            </p>
            <Select onValueChange={handleSelectOrg}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select Xero organization..." />
              </SelectTrigger>
              <SelectContent>
                {availableOrgs.map((org: any) => (
                  <SelectItem key={org.xero_tenant_id} value={org.xero_tenant_id}>
                    {org.xero_tenant_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-7 text-xs text-muted-foreground"
              onClick={handleConnectNewOrg}
              disabled={isConnecting}
            >
              {isConnecting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Link2 className="h-3 w-3 mr-1" />}
              Connect a new Xero org
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Connect your Xero account first, then select the organization for this client.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full h-8 text-xs"
              onClick={handleConnectNewOrg}
              disabled={isConnecting}
            >
              {isConnecting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Link2 className="h-3 w-3 mr-1" />}
              Connect to Xero
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================================
// Accounting Integration — unified picker (Xero / QuickBooks / MYOB)
// One client = one accounting source. Pick visually rather than two stacked
// connect-flows so the UX scales as we add more providers.
// ============================================================================

type ProviderId = 'xero' | 'qbo' | 'myob';
interface Provider {
  id: ProviderId;
  name: string;
  shortName: string;
  available: boolean;
  brand: string;       // background colour
  brandText: string;   // text colour over brand
  mark: string;        // single-letter mark for the logo tile
}
const PROVIDERS: Provider[] = [
  { id: 'xero', name: 'Xero',          shortName: 'Xero', available: true,  brand: '#13B5EA', brandText: '#FFFFFF', mark: 'X' },
  { id: 'qbo',  name: 'QuickBooks',    shortName: 'QBO',  available: true,  brand: '#2CA01C', brandText: '#FFFFFF', mark: 'qb' },
  { id: 'myob', name: 'MYOB',          shortName: 'MYOB', available: false, brand: '#6E2A8C', brandText: '#FFFFFF', mark: 'M' },
];

function ProviderLogo({ provider, size = 32 }: { provider: Provider; size?: number }) {
  return (
    <div
      className="rounded-md flex items-center justify-center font-bold shrink-0 select-none"
      style={{
        backgroundColor: provider.brand,
        color: provider.brandText,
        width: size,
        height: size,
        fontSize: provider.mark.length > 1 ? Math.floor(size * 0.42) : Math.floor(size * 0.55),
        letterSpacing: '-0.03em',
      }}
    >
      {provider.mark}
    </div>
  );
}

function AccountingIntegrationSection({ client, onMapped }: { client: Client; onMapped: () => void }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<ProviderId | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [picking, setPicking] = useState(false);
  const [xeroExpanded, setXeroExpanded] = useState(false);

  // Per-client QBO connection status (Xero comes from the client object directly)
  const { data: qboStatus } = useQuery({
    queryKey: ['qbo-status', client.id],
    queryFn: async () => {
      const resp = await apiClient.get(`/integrations/qbo/status?client_id=${client.id}`);
      return resp.data?.data || resp.data;
    },
    enabled: !!client.id,
  });
  const { data: xeroStatus } = useQuery({
    queryKey: ['xero-status'],
    queryFn: getConnectionStatus,
  });

  const xeroOrgs = xeroStatus?.organizations || [];
  const firmHasXeroOAuth = xeroOrgs.length > 0;
  const qboConnected = !!qboStatus?.connected;
  const qboName = qboStatus?.companyName || (client as any).qbo_company_name;
  const xeroConnected = !!client.xeroOrgName;

  // Active provider: QBO wins when both happen to be set (matches backend precedence)
  const activeProvider: Provider | null = qboConnected
    ? PROVIDERS.find(p => p.id === 'qbo')!
    : xeroConnected
      ? PROVIDERS.find(p => p.id === 'xero')!
      : null;
  const activeName = activeProvider?.id === 'qbo' ? qboName : client.xeroOrgName;

  const mapClientToXeroOrg = async (xeroTenantId: string) => {
    setBusy('xero');
    try {
      const org = xeroOrgs.find((o: any) => o.xero_tenant_id === xeroTenantId);
      await mapXeroClient(client.id, xeroTenantId, org?.xero_tenant_name);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      onMapped();
      toast.success(`Connected to ${org?.xero_tenant_name || 'Xero org'}`);
      setPicking(false);
      setXeroExpanded(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to map Xero org');
    } finally {
      setBusy(null);
    }
  };

  const startXeroOAuth = async () => {
    setBusy('xero');
    try {
      const returnUrl = `/panel/files/clients?selected=${client.id}`;
      const { authUrl } = await connectXero(returnUrl, client.id);
      window.location.href = authUrl;
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to start Xero authorisation');
      setBusy(null);
    }
  };

  const connectQboFlow = async () => {
    setBusy('qbo');
    try {
      const returnUrl = `/panel/files/clients?selected=${client.id}`;
      const resp = await apiClient.get(
        `/integrations/qbo/connect?client_id=${client.id}&return_url=${encodeURIComponent(returnUrl)}`
      );
      const data = resp.data?.data || resp.data;
      if (!data?.authUrl) throw new Error('No auth URL returned');
      window.location.href = data.authUrl;
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Failed to connect QuickBooks');
      setBusy(null);
    }
  };

  const handleDisconnect = async () => {
    if (!activeProvider) return;
    if (!confirm(`Disconnect ${activeProvider.name}${activeName ? ' (' + activeName + ')' : ''} from this client?`)) return;
    setDisconnecting(true);
    try {
      if (activeProvider.id === 'qbo') {
        await apiClient.post(`/integrations/qbo/disconnect`, { client_id: client.id });
      } else {
        await mapXeroClient(client.id, null);
      }
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['qbo-status', client.id] });
      onMapped();
      toast.success(`${activeProvider.name} disconnected`);
      setPicking(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || `Failed to disconnect ${activeProvider.name}`);
    } finally {
      setDisconnecting(false);
    }
  };

  // Picker: vertical list of provider tiles. Clicking Xero either expands an
  // org-picker (if firm already has Xero OAuth orgs) or starts OAuth directly.
  const renderPicker = () => (
    <div className="space-y-1.5">
      {PROVIDERS.map(p => {
        const isXero = p.id === 'xero';
        const isQbo = p.id === 'qbo';
        const isCurrent = activeProvider?.id === p.id;
        const isDisabled = !p.available || isCurrent || busy !== null;
        const xeroIsExpanded = isXero && xeroExpanded;

        const subtitle = !p.available
          ? 'Coming soon'
          : isXero
            ? firmHasXeroOAuth
              ? xeroIsExpanded
                ? 'Select which org to map to this client'
                : `${xeroOrgs.length} org${xeroOrgs.length === 1 ? '' : 's'} available — click to choose`
              : 'Click to authorise the firm with Xero'
            : 'Click to authorise this client with QuickBooks';

        return (
          <div key={p.id}>
            <div
              className={cn(
                'flex items-center gap-2.5 rounded-md border p-2 transition-colors',
                !p.available && 'opacity-60',
                xeroIsExpanded && 'rounded-b-none border-b-0',
                !isDisabled && 'hover:border-foreground/20 hover:bg-muted/30 cursor-pointer'
              )}
              onClick={() => {
                if (isDisabled) return;
                if (isXero && firmHasXeroOAuth) {
                  setXeroExpanded(v => !v);
                } else if (isXero) {
                  startXeroOAuth();
                } else if (isQbo) {
                  connectQboFlow();
                }
              }}
            >
              <ProviderLogo provider={p} size={28} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-tight">{p.name}</p>
                <p className="text-[11px] text-muted-foreground">{subtitle}</p>
              </div>
              {busy === p.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              ) : isCurrent ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
              ) : p.available ? (
                <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
              ) : null}
            </div>

            {xeroIsExpanded && (
              <div className="rounded-md rounded-t-none border border-t-0 bg-muted/10 p-2 space-y-1.5">
                <Select onValueChange={(v) => mapClientToXeroOrg(v)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Select Xero organisation…" />
                  </SelectTrigger>
                  <SelectContent>
                    {xeroOrgs.map((o: any) => (
                      <SelectItem key={o.xero_tenant_id} value={o.xero_tenant_id}>
                        {o.xero_tenant_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-full text-[11px] text-muted-foreground"
                  onClick={(e) => { e.stopPropagation(); startXeroOAuth(); }}
                >
                  + Authorise an additional Xero org
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <Separator />
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
            <Link2 className="h-4 w-4" />
            Accounting System
          </h4>
          {activeProvider && !picking && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => setPicking(true)}
            >
              Change
            </Button>
          )}
        </div>

        {activeProvider && !picking ? (
          <div className="flex items-center gap-2.5 rounded-md border bg-muted/20 p-2">
            <ProviderLogo provider={activeProvider} size={32} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium truncate">{activeProvider.name}</p>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
              </div>
              <p className="text-[11px] text-muted-foreground truncate">
                {activeName || 'Connected'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-[11px]"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Unlink className="h-3 w-3 mr-1" />}
              Disconnect
            </Button>
          </div>
        ) : (
          <>
            {activeProvider && picking && (
              <div className="rounded-md border bg-amber-500/5 border-amber-500/30 p-2 text-[11px] text-amber-800 mb-1">
                Switching providers will disconnect <strong>{activeProvider.name}</strong> first.
                <Button variant="link" size="sm" className="h-auto p-0 ml-1 text-[11px]" onClick={() => setPicking(false)}>
                  Cancel
                </Button>
              </div>
            )}
            {renderPicker()}
          </>
        )}
      </div>
    </>
  );
}

function QboMappingSection({ client, onMapped }: { client: Client; onMapped: () => void }) {
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Per-client QBO connection status
  const { data: qboStatus } = useQuery({
    queryKey: ['qbo-status', client.id],
    queryFn: async () => {
      const resp = await apiClient.get(`/integrations/qbo/status?client_id=${client.id}`);
      return resp.data?.data || resp.data;
    },
    enabled: !!client.id,
  });

  const isConnected = !!qboStatus?.connected;
  const companyName = qboStatus?.companyName || (client as any).qbo_company_name;

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const returnUrl = `/panel/clients?selected=${client.id}`;
      const resp = await apiClient.get(
        `/integrations/qbo/connect?client_id=${client.id}&return_url=${encodeURIComponent(returnUrl)}`
      );
      const data = resp.data?.data || resp.data;
      if (!data?.authUrl) throw new Error('No auth URL returned');
      window.location.href = data.authUrl;
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Failed to connect to QuickBooks');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm(`Disconnect ${companyName || 'QuickBooks'} from this client?`)) return;
    setIsDisconnecting(true);
    try {
      await apiClient.post(`/integrations/qbo/disconnect`, { client_id: client.id });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['qbo-status', client.id] });
      onMapped();
      toast.success('QuickBooks disconnected');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to disconnect');
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <>
      <Separator />
      <div className="space-y-3">
        <h4 className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
          <Link2 className="h-4 w-4" />
          QuickBooks Online
        </h4>
        {isConnected ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-sm truncate">{companyName || 'Connected'}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs flex-shrink-0"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Unlink className="h-3 w-3 mr-1" />}
              Disconnect
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {client.xeroOrgName
                ? 'Connecting QuickBooks will switch this client to QuickBooks for chat + automation.'
                : 'Connect this client to a QuickBooks Online company to load financials in chat and document automation.'}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full h-8 text-xs"
              onClick={handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Link2 className="h-3 w-3 mr-1" />}
              Connect to QuickBooks
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

// ATO Small Business Benchmark Industries (source: ato.gov.au/benchmarks-a-z)
const ATO_INDUSTRIES = [
  'Air conditioning, refrigeration and heating services',
  'Alarm systems installation - fire and security',
  'Architectural services',
  'Automotive electrical services',
  'Bakeries and hot bread shops',
  'Barber and men\'s hairdressing',
  'Beauty services',
  'Blocklaying services',
  'Book retailing',
  'Bottle shops and liquor retailing',
  'Bricklaying services',
  'Cabinet makers',
  'Cake shops and patisseries',
  'Carpentry services',
  'Carpet laying services',
  'Catering services',
  'Cement rendering',
  'Chicken shops',
  'Child care services',
  'Chiropractic and osteopathic services',
  'Cleaning services - building and other industrial',
  'Cleaning services - carpet, rug and upholstery',
  'Clothing retailing',
  'Coffee shops',
  'Computer retailing',
  'Concreting services',
  'Confectionery retailing',
  'Courier services',
  'Craft shops',
  'Delicatessen',
  'Delivery services',
  'Dental specialists',
  'Dental surgeons - general',
  'Discount and variety stores',
  'Domestic appliance repair and maintenance',
  'Driving schools and instructors',
  'Electrical and electronic product retailing',
  'Electrical services',
  'Entertainment media retailing',
  'Fence construction',
  'Fish and chips shops',
  'Fish and seafood retailing - fresh',
  'Floor covering retailing',
  'Florists',
  'Footwear retailing',
  'Fruit and vegetable retailing',
  'Fuel retailing',
  'Furniture removalists',
  'Furniture retailing',
  'Garden supplies retailing',
  'Gift stores',
  'Glazing services',
  'Grocery retailing and convenience stores',
  'Hairdressers',
  'Hardware and building supplies retailing',
  'Health and fitness centres',
  'Health food retailing',
  'Homewares retailing',
  'Ice cream retailing',
  'Kebab shops',
  'Landscape construction',
  'Laundry and dry-cleaning services',
  'Lawn mower retailing',
  'Lawn mowing and garden services',
  'Machinery and equipment repair and maintenance',
  'Manchester and other textile goods retailing',
  'Meat and poultry retailing - fresh',
  'Motor vehicle parts and batteries retailing',
  'Motor vehicle retail - new and used',
  'Musical instruments retail',
  'Newsagents',
  'Painting services',
  'Panel beating and smash repairs',
  'Pest control services',
  'Pets and pet supply retailing',
  'Pharmacy',
  'Physiotherapy services',
  'Picture framing retailing',
  'Pizza shops - takeaway',
  'Plastering and ceiling services',
  'Plumbing services',
  'Printing',
  'Printing support services',
  'Pubs, taverns and bars',
  'Restaurants',
  'Road freight transport services',
  'Roofing services',
  'Sports, camping and fishing retailing',
  'Sports and physical recreation instruction',
  'Stationery goods retailing',
  'Takeaway food services',
  'Taxi drivers and operators',
  'Tiling services - floor and wall',
  'Timber floor sanding',
  'Tobacco retailing',
  'Towing services',
  'Toy and game retailing',
  'Tutoring and coaching',
  'Tyre retailing',
  'Veterinary services',
  'Video and other electronic media rental',
  'Watch and jewellery retailing',
  // Additional common industries not in ATO benchmarks
  'Accounting and tax services',
  'Construction services',
  'Engineering services',
  'Financial planning',
  'Import and export trading',
  'IT consulting and services',
  'Legal services',
  'Manufacturing',
  'Medical practice',
  'Property and real estate services',
  'Real estate services',
  'Supermarket and grocery stores',
  'Transport and logistics',
  'Wholesale trading',
  'Other',
];

export function ClientsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<ClientStatus | 'all'>(
    (searchParams.get('status') as ClientStatus) || 'all'
  );
  const [typeFilter, setTypeFilter] = useState<ClientType | 'all'>('all');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<UpdateClientRequest>({});

  // Create form state
  const [createForm, setCreateForm] = useState<CreateClientRequest>({
    clientCode: '',
    displayName: '',
    clientType: 'company',
    email: '',
    phone: '',
    address: '',
    abn: '',
    industry: '',
  });

  // Fetch clients
  const { data: clientsData, isLoading, refetch } = useQuery({
    queryKey: ['clients', search, statusFilter, typeFilter],
    queryFn: () => clientsService.listClients({
      search: search || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      clientType: typeFilter !== 'all' ? typeFilter : undefined,
    }),
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['client-stats'],
    queryFn: () => clientsService.getClientStats(),
  });

  const clients = clientsData?.clients || [];
  const stats = statsData;

  // Create client mutation
  const createMutation = useMutation({
    mutationFn: (input: CreateClientRequest) => clientsService.createClient(input),
    onSuccess: () => {
      toast.success('Client created successfully!');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-stats'] });
      setCreateDialogOpen(false);
      resetCreateForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create client');
    },
  });

  // Delete client mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientsService.deleteClient(id),
    onSuccess: () => {
      toast.success('Client deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-stats'] });
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      if (selectedClient?.id === clientToDelete?.id) {
        setSelectedClient(null);
      }
    },
    onError: () => {
      toast.error('Failed to delete client');
    },
  });

  // Update client mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientRequest }) =>
      clientsService.updateClient(id, data),
    onSuccess: (updatedClient) => {
      toast.success('Client updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setEditDialogOpen(false);
      setSelectedClient(updatedClient);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update client');
    },
  });

  const openEditDialog = (client: Client) => {
    setEditForm({
      displayName: client.displayName,
      clientType: client.clientType,
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      abn: client.abn || '',
      acn: client.acn || '',
      tfn: client.tfn || '',
      industry: client.industry || '',
    });
    setEditDialogOpen(true);
  };

  const resetCreateForm = () => {
    setCreateForm({
      clientCode: '',
      displayName: '',
      clientType: 'company',
      email: '',
      phone: '',
      address: '',
      abn: '',
      industry: '',
    });
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setSearchParams(prev => {
      if (value) prev.set('search', value);
      else prev.delete('search');
      return prev;
    });
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value as ClientStatus | 'all');
    setSearchParams(prev => {
      if (value !== 'all') prev.set('status', value);
      else prev.delete('status');
      return prev;
    });
  };

  const getStatusBadge = (status: ClientStatus) => {
    const config: Record<ClientStatus, { label: string; className: string }> = {
      active: { label: 'Active', className: 'bg-green-500/10 text-green-500' },
      inactive: { label: 'Inactive', className: 'bg-gray-500/10 text-gray-500' },
      archived: { label: 'Archived', className: 'bg-amber-500/10 text-amber-500' },
    };
    const { label, className } = config[status] || config.active;
    return <Badge className={className}>{label}</Badge>;
  };

  const getTypeBadge = (type: ClientType) => {
    const labels: Record<ClientType, string> = {
      individual: 'Individual',
      company: 'Company',
      trust: 'Trust',
      smsf: 'SMSF',
      partnership: 'Partnership',
    };
    return <Badge variant="outline">{labels[type] || type}</Badge>;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getHealthScoreBadge = (healthScore: ClientHealthScore) => {
    const config: Record<HealthScoreLevel, { icon: React.ReactNode; className: string; label: string }> = {
      excellent: {
        icon: <CheckCircle2 className="h-3 w-3" />,
        className: 'bg-green-500/10 text-green-600 border-green-500/20',
        label: 'Excellent',
      },
      good: {
        icon: <TrendingUp className="h-3 w-3" />,
        className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
        label: 'Good',
      },
      needs_attention: {
        icon: <AlertTriangle className="h-3 w-3" />,
        className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
        label: 'Needs Attention',
      },
      critical: {
        icon: <TrendingDown className="h-3 w-3" />,
        className: 'bg-red-500/10 text-red-600 border-red-500/20',
        label: 'Critical',
      },
    };

    const { icon, className, label } = config[healthScore.level];

    return (
      <Badge className={cn('flex items-center gap-1 text-xs font-medium border', className)}>
        {icon}
        <span>{healthScore.score}</span>
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={Users}
        title="Clients"
        description="Manage client files and documents"
        actions={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Add Client
          </Button>
        }
      />


      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Clients</p>
                  <p className="text-2xl font-bold">{stats.totalClients}</p>
                </div>
                <Users className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.activeClients}</p>
                </div>
                <FileCheck className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Documents</p>
                  <p className="text-2xl font-bold">{stats.totalDocuments.toLocaleString()}</p>
                </div>
                <FileText className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Size</p>
                  <p className="text-2xl font-bold">{formatSize(stats.totalSizeBytes)}</p>
                </div>
                <HardDrive className="h-8 w-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients by name, code, email, or ABN..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as ClientType | 'all')}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="trust">Trust</SelectItem>
                <SelectItem value="smsf">SMSF</SelectItem>
                <SelectItem value="partnership">Partnership</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Main Content - 1/3 client list, 2/3 chat */}
      <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
        {/* Clients List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-4">
            <div>
              <CardTitle className="text-base">Clients</CardTitle>
              <CardDescription>{clientsData?.total || 0} clients found</CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="font-medium">No clients found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {search || statusFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Add your first client to get started'
                  }
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className={cn(
                      'flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors',
                      selectedClient?.id === client.id && 'bg-muted/50'
                    )}
                    onClick={() => setSelectedClient(client)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{client.displayName}</p>
                        {getTypeBadge(client.clientType)}
                        {client.healthScore && getHealthScoreBadge(client.healthScore)}
                        {client.xeroOrgId && (
                          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-[10px] h-5 flex items-center gap-0.5">
                            <Link2 className="h-2.5 w-2.5" />
                            Xero
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {client.clientCode} {client.email && `• ${client.email}`}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {client.totalDocuments} docs
                        </span>
                        <span className="flex items-center gap-1">
                          <HardDrive className="h-3 w-3" />
                          {formatSize(client.totalSizeBytes)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      {getStatusBadge(client.status)}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/panel/files/clients/${client.id}`)}>
                            <FolderOpen className="mr-2 h-4 w-4" />
                            Open Client Workspace
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelectedClient(client); openEditDialog(client); }}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit Client
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setClientToDelete(client);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client Details Panel */}
        <Card className="h-fit sticky top-20">
          <CardHeader>
            <CardTitle className="text-base">Client Details</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedClient ? (
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">{selectedClient.displayName}</h3>
                    {getStatusBadge(selectedClient.status)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{selectedClient.clientCode}</Badge>
                    {getTypeBadge(selectedClient.clientType)}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => navigate(`/panel/files/clients/${selectedClient.id}`)}
                  >
                    <FolderOpen className="mr-2 h-4 w-4" />
                    Open Client Workspace
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => openEditDialog(selectedClient)}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                </div>

                {/* Accounting integration — unified picker (Xero / QBO / MYOB) */}
                <AccountingIntegrationSection
                  client={selectedClient}
                  onMapped={() => refetch()}
                />

                <Separator />

                {/* Contact Info */}
                <div className="space-y-2.5">
                  <h4 className="text-sm font-medium text-muted-foreground">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    {selectedClient.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4 flex-shrink-0" />
                        <a href={`mailto:${selectedClient.email}`} className="hover:text-primary truncate">
                          {selectedClient.email}
                        </a>
                      </div>
                    )}
                    {selectedClient.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4 flex-shrink-0" />
                        <span>{selectedClient.phone}</span>
                      </div>
                    )}
                    {selectedClient.abn && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Building2 className="h-4 w-4 flex-shrink-0" />
                        <span>ABN: {selectedClient.abn.replace(/\D/g, '').replace(/^(\d{2})(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3 $4')}</span>
                      </div>
                    )}
                    {selectedClient.industry && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <FileText className="h-4 w-4 flex-shrink-0" />
                        <span>{selectedClient.industry}</span>
                      </div>
                    )}

                  </div>
                </div>

                <Separator />

                {/* Document Stats */}
                <div className="space-y-2.5">
                  <h4 className="text-sm font-medium text-muted-foreground">Documents</h4>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="space-y-1">
                      <p className="text-2xl font-semibold">{selectedClient.totalDocuments}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-2xl font-semibold">{selectedClient.indexedDocuments}</p>
                      <p className="text-xs text-muted-foreground">Indexed</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-2xl font-semibold text-sm">{formatSize(selectedClient.totalSizeBytes)}</p>
                      <p className="text-xs text-muted-foreground">Storage</p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Health Score Detail */}
                {selectedClient.healthScore && (
                  <>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-muted-foreground">File Health Score</h4>
                        {getHealthScoreBadge(selectedClient.healthScore)}
                      </div>
                      <div className="space-y-2 text-sm">
                        {selectedClient.healthScore.issues.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground">Issues ({selectedClient.healthScore.issues.length})</p>
                            {selectedClient.healthScore.issues.slice(0, 3).map((issue) => (
                              <div key={issue.id} className="flex items-start gap-2 text-xs">
                                {issue.severity === 'critical' && <AlertTriangle className="h-3 w-3 text-red-500 flex-shrink-0 mt-0.5" />}
                                {issue.severity === 'warning' && <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0 mt-0.5" />}
                                {issue.severity === 'info' && <Activity className="h-3 w-3 text-blue-500 flex-shrink-0 mt-0.5" />}
                                <span className="text-muted-foreground">{issue.title}</span>
                              </div>
                            ))}
                            {selectedClient.healthScore.issues.length > 3 && (
                              <p className="text-xs text-muted-foreground/70 pl-5">
                                +{selectedClient.healthScore.issues.length - 3} more issues
                              </p>
                            )}
                          </div>
                        )}
                        {selectedClient.healthScore.recommendations.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground">Top Recommendations</p>
                            {selectedClient.healthScore.recommendations.slice(0, 2).map((rec, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-xs">
                                <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                                <span className="text-muted-foreground">{rec}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <Separator />
                  </>
                )}

                {/* Deadlines */}
                <ClientDeadlines clientId={selectedClient.id} />

                {/* Service Description */}
                {selectedClient.serviceDescription && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                        <Briefcase className="h-4 w-4" />
                        Services
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {selectedClient.serviceDescription}
                      </p>
                    </div>
                  </>
                )}

                {/* Work History */}
                {selectedClient.workHistory && selectedClient.workHistory.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                        <History className="h-4 w-4" />
                        Recent Activity
                      </h4>
                      <ScrollArea className="h-[160px] pr-4">
                        <div className="space-y-3">
                          {selectedClient.workHistory.map((item, idx) => (
                            <div key={idx} className="flex gap-3 text-sm">
                              <div className="flex-shrink-0 mt-0.5">
                                {item.type === 'tax_lodgement' && (
                                  <Receipt className="h-4 w-4 text-green-500" />
                                )}
                                {item.type === 'bas_lodgement' && (
                                  <ClipboardList className="h-4 w-4 text-blue-500" />
                                )}
                                {item.type === 'meeting' && (
                                  <MessageSquare className="h-4 w-4 text-purple-500" />
                                )}
                                {item.type === 'correspondence' && (
                                  <Mail className="h-4 w-4 text-orange-500" />
                                )}
                                {item.type === 'audit' && (
                                  <CheckCircle2 className="h-4 w-4 text-amber-500" />
                                )}
                                {item.type === 'review' && (
                                  <FileText className="h-4 w-4 text-cyan-500" />
                                )}
                                {item.type === 'other' && (
                                  <Clock className="h-4 w-4 text-gray-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-muted-foreground">{item.description}</p>
                                <p className="text-xs text-muted-foreground/70 mt-0.5">
                                  {formatDate(item.date)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  </>
                )}

                <Separator />

                {/* Dates */}
                <div className="space-y-2 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Client Since</span>
                    <span>{formatDate(selectedClient.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Last Updated</span>
                    <span>{formatDate(selectedClient.updatedAt)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a client to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Client Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
            <DialogDescription>
              Create a new client to manage their files and documents.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="clientCode">Client Code *</Label>
                <Input
                  id="clientCode"
                  placeholder="ABC001"
                  value={createForm.clientCode}
                  onChange={(e) => setCreateForm({ ...createForm, clientCode: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clientType">Type *</Label>
                <Select
                  value={createForm.clientType}
                  onValueChange={(v) => setCreateForm({ ...createForm, clientType: v as ClientType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="trust">Trust</SelectItem>
                    <SelectItem value="smsf">SMSF</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name *</Label>
              <Input
                id="displayName"
                placeholder="ABC Pty Ltd"
                value={createForm.displayName}
                onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="contact@example.com"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                placeholder="+61 4XX XXX XXX"
                value={createForm.phone}
                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="abn">ABN</Label>
              <Input
                id="abn"
                placeholder="XX XXX XXX XXX"
                value={createForm.abn}
                onChange={(e) => setCreateForm({ ...createForm, abn: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry">Industry (ATO Benchmark)</Label>
              <Select value={createForm.industry} onValueChange={(v) => setCreateForm({ ...createForm, industry: v })}>
                <SelectTrigger id="industry">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {ATO_INDUSTRIES.map((ind) => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="123 Business St, Sydney NSW 2000"
                value={createForm.address}
                onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate(createForm)}
              disabled={!createForm.clientCode || !createForm.displayName || createMutation.isPending}
            >
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteConfirmText(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Client Permanently
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>You are about to permanently delete <strong>{clientToDelete?.displayName}</strong>.</p>
              <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-xs space-y-1">
                <p className="font-semibold text-destructive">This will permanently remove:</p>
                <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
                  <li>All uploaded documents and files</li>
                  <li>All chat conversations and history</li>
                  <li>All compliance and audit data</li>
                  <li>Xero connection for this client</li>
                </ul>
                <p className="font-semibold text-destructive pt-1">This action cannot be undone.</p>
              </div>
              <div className="pt-1">
                <p className="text-xs text-muted-foreground mb-1.5">
                  Type <strong className="text-foreground">{clientToDelete?.displayName}</strong> to confirm:
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type client name to confirm..."
                  className="text-sm"
                  autoFocus
                />
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmText(''); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => clientToDelete && deleteMutation.mutate(clientToDelete.id)}
              disabled={deleteMutation.isPending || deleteConfirmText !== clientToDelete?.displayName}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
            <DialogDescription>
              Update client details for {selectedClient?.displayName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editDisplayName">Display Name *</Label>
                <Input
                  id="editDisplayName"
                  value={editForm.displayName || ''}
                  onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editClientType">Type</Label>
                <Select
                  value={editForm.clientType || 'company'}
                  onValueChange={(v) => setEditForm({ ...editForm, clientType: v as ClientType })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="trust">Trust</SelectItem>
                    <SelectItem value="smsf">SMSF</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="editEmail">Email Address</Label>
              <Input
                id="editEmail"
                type="email"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="editPhone">Phone Number</Label>
              <Input
                id="editPhone"
                value={editForm.phone || ''}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editAbn">ABN</Label>
                <Input
                  id="editAbn"
                  placeholder="XX XXX XXX XXX"
                  value={editForm.abn || ''}
                  onChange={(e) => setEditForm({ ...editForm, abn: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editAcn">ACN</Label>
                <Input
                  id="editAcn"
                  placeholder="XXX XXX XXX"
                  value={editForm.acn || ''}
                  onChange={(e) => setEditForm({ ...editForm, acn: e.target.value })}
                />
              </div>
            </div>


            <div className="space-y-2">
              <Label htmlFor="editTfn">TFN</Label>
              <Input
                id="editTfn"
                placeholder="XXX XXX XXX"
                value={editForm.tfn || ''}
                onChange={(e) => setEditForm({ ...editForm, tfn: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="editIndustry">Industry (ATO Benchmark)</Label>
              <Select value={editForm.industry || ''} onValueChange={(v) => setEditForm({ ...editForm, industry: v })}>
                <SelectTrigger id="editIndustry">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {ATO_INDUSTRIES.map((ind) => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="editAddress">Address</Label>
              <Input
                id="editAddress"
                value={editForm.address || ''}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => selectedClient && updateMutation.mutate({ id: selectedClient.id, data: editForm })}
              disabled={!editForm.displayName || updateMutation.isPending}
            >
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
